/** Disposable inventory snapshots. Never written to the application's inventory store. */
import { useSyncExternalStore } from 'react';
import i18next from 'i18next';
import type { AvailableProduct, CountReviewResponse, CountReviewProduct, InventoryCount, InventorySectionProduct, InventoryGroup, InventorySection, InventorySessionSummary } from '../../../services/inventory/inventoryService';
import type { CanonicalCountUnit } from '../../../utils/reports/inventoryVariance';
import { buildPracticeProducts, PRACTICE_PLATFORM } from './costPriceFixture';
import { inventoryKey } from '../../../utils/reports/consumptionReport';
import { PRACTICE_ID_PREFIX } from './practiceGuards';
export const INVENTORY_PRACTICE_PREFIX = `${PRACTICE_ID_PREFIX}inventory-`;
const id = (name: string) => `${INVENTORY_PRACTICE_PREFIX}${name}`;
export type InventoryPracticeSelection = {
    groupId: string | null;
    sessionId: string | null;
    baselineSessionId?: string | null;
};
export interface InventoryPracticeDataset {
    source?: 'customer' | 'fixture';
    restaurantId: string;
    groups: InventoryGroup[];
    sections: InventorySection[];
    countsHistory: InventoryCount[];
    sectionProducts: Record<string, InventorySectionProduct[]>;
    availableProducts: AvailableProduct[];
    sessionSummaries: InventorySessionSummary[];
    canonicalUnitsByGroup: Record<string, Map<string, CanonicalCountUnit>>;
    countReview: {
        data: CountReviewResponse;
        selection: InventoryPracticeSelection;
        isLoading: false;
        loadedKey: string;
    };
}
let dataset: InventoryPracticeDataset | null = null;
let revision = 0;
const listeners = new Set<() => void>();
export const subscribeInventoryPractice = (listener: () => void) => {
    listeners.add(listener);
    return () => { listeners.delete(listener); };
};
export const inventoryPracticeRevision = () => revision;
export const getInventoryPracticeDataset = () => dataset;
export const useInventoryPracticeDataset = () => useSyncExternalStore(subscribeInventoryPractice, getInventoryPracticeDataset, getInventoryPracticeDataset);
function publish() {
    revision++;
    listeners.forEach(listener => listener());
}
export const isInventoryPracticeActive = () => dataset !== null;
export const isCustomerInventoryPractice = () => dataset?.source === 'customer';

/** Preserve the server's frozen valuations and classifications without rebuilding them. */
export function beginCustomerInventoryPracticeData(input: InventoryPracticeDataset) {
    dataset = structuredClone({ ...input, source: 'customer' });
    publish();
}
export function isInventoryPracticeReference(value: unknown): boolean {
    if (typeof value === 'string')
        return value.includes(INVENTORY_PRACTICE_PREFIX);
    if (Array.isArray(value))
        return value.some(isInventoryPracticeReference);
    if (value && typeof value === 'object')
        return Object.values(value).some(isInventoryPracticeReference);
    return false;
}
/** Service barrier: all fixture writes stay in the facade; retired fixture IDs fail closed. */
export function rejectInventoryPracticeWrite(...values: unknown[]) {
    if (dataset || values.some(isInventoryPracticeReference))
        throw new Error('Inventory practice writes must remain in the disposable session');
}
function review(data: Omit<InventoryPracticeDataset, 'countReview'>, selection: InventoryPracticeSelection): CountReviewResponse {
    const latest = selection.sessionId ?? data.sessionSummaries[0]?.sessionId;
    const counts = data.countsHistory.filter(c => c.sessionId === latest && (!selection.groupId || c.groupId === selection.groupId));
    const products: CountReviewProduct[] = counts.flatMap(count => count.items.map(item => {
        const assignment = data.sectionProducts[count.inventorySectionId!]!.find(p => p.productKey === item.productKey)!;
        return {
            ...item, countId: count.id, sectionId: count.inventorySectionId, sectionName: count.sectionNameSnapshot,
            inventorySectionProductId: assignment.id, groupId: count.groupId, groupName: count.groupNameSnapshot,
            inventoryCategoryId: assignment.inventoryCategoryId ?? null, inventoryCategoryName: assignment.inventoryCategoryName ?? null,
            productCategory: data.availableProducts.find(p => p.productKey === item.productKey)?.category ?? 'VEGETABLES', mainCategory: 'FOOD', subCategories: [],
            quantityDisplay: { value: item.countInStdUnit / 1000, unit: 'kg' }, unitPrice: item.pricePerStdUnit * 1000,
            stdUnitPrice: { value: item.pricePerStdUnit * 1000, unit: 'kg' }, supplier: { platform: PRACTICE_PLATFORM, name: null },
            source: assignment.source, linked: assignment.source !== 'custom', missingPrice: item.pricePerStdUnit === 0, belowMinimum: false, neverCounted: false
        };
    }));
    const totalValue = products.reduce((sum, p) => sum + p.value, 0);
    const previous = data.sessionSummaries.findIndex(s => s.sessionId === latest) + 1;
    const previousTotalValue = data.sessionSummaries[previous]?.totalValue ?? null;
    const sections = counts.map(c => ({
        sectionId: c.inventorySectionId, sectionName: c.sectionNameSnapshot, countId: c.id, groupId: c.groupId, groupName: c.groupNameSnapshot,
        totalValue: c.totalValue, share: totalValue ? c.totalValue / totalValue : 0, productCount: c.items.length, belowMinCount: 0, lastCountedAt: c.countedAt.toISOString(), changeValue: null
    }));
    const categoriesInventory = Array.from(new Set(products.map(p => p.inventoryCategoryId))).map(categoryId => {
        const rows = products.filter(p => p.inventoryCategoryId === categoryId);
        const value = rows.reduce((s, p) => s + p.value, 0);
        return {
            categoryId, name: rows[0].inventoryCategoryName!, totalValue: value, share: totalValue ? value / totalValue : 0, productCount: rows.length, belowMinCount: 0
        };
    }).sort((a, b) => b.totalValue - a.totalValue);
    const categoriesProduct = Array.from(new Set(products.map(p => p.productCategory!))).map(key => {
        const rows = products.filter(p => p.productCategory === key);
        const value = rows.reduce((s, p) => s + p.value, 0);
        return {
            key, mainCategory: 'FOOD', totalValue: value, share: totalValue ? value / totalValue : 0, productCount: rows.length, belowMinCount: 0, subCategories: []
        };
    });
    const needsReview = products.filter(p => p.count >= 20).map(p => ({
        productKey: p.productKey, productName: p.productName, groupId: p.groupId, groupName: p.groupName,
        sections: [p.sectionName], sectionIds: [p.sectionId!], countUnit: p.countUnit, currentCount: p.count, expectedCount: 2, expectedSource: 'typical' as const,
        previousCount: 2, sessionsCompared: 3, ratio: p.count / 2, unitPrice: p.unitPrice!, value: p.value, impact: (p.count - 2) * p.unitPrice!, reasons: [{ kind: 'spike' as const, params: {} }], supplier: p.supplier
    }));
    return {
        selection: { ...selection, completedAt: counts[0]?.countedAt.toISOString() ?? null },
        kpis: {
            totalValue, previousTotalValue, changeValue: previousTotalValue === null ? null : totalValue - previousTotalValue, productsCount: products.length, notLinkedCount: products.filter(p => !p.linked).length, needsOrderingCount: 0
        },
        groups: data.groups.filter(g => !selection.groupId || selection.groupId === g.id).map(g => ({
            groupId: g.id, groupName: g.name, totalValue, share: 1, sectionCount: sections.length, productCount: products.length, belowMinCount: 0, changeValue: null
        })),
        sections, categoriesInventory, categoriesProduct, products, needsReview, belowMinProducts: [], productMinimums: [], configMissingPrice: [],
        baselineOptions: data.sessionSummaries.slice(previous).map(s => ({ sessionId: s.sessionId, completedAt: s.completedAt.toISOString(), totalValue: s.totalValue }))
    };
}
function rebuild(data: InventoryPracticeDataset) {
    const sessionSummaries = data.sessionSummaries.map(s => {
        const counts = data.countsHistory.filter(c => c.sessionId === s.sessionId);
        return { ...s, totalValue: counts.reduce((n, c) => n + c.totalValue, 0), productCount: counts.reduce((n, c) => n + c.items.length, 0) };
    });
    const next = { ...data, sessionSummaries };
    dataset = { ...next, countReview: { ...next.countReview, data: review(next, next.countReview.selection) } };
    publish();
}
export function beginInventoryPracticeData(restaurantId: string) {
    const now = new Date();
    now.setHours(23, 59, 0, 0);
    const group: InventoryGroup = {
        id: id('group'), restaurantId, name: i18next.t('onboarding.inventory.practice.group'), groupType: 'food', position: 0, createdAt: now, updatedAt: now
    };
    const sections: InventorySection[] = ['kitchen', 'storage', 'cellar'].map((key, index) => ({
        id: id(key), restaurantId, groupId: group.id, sectionName: i18next.t(`onboarding.inventory.practice.${key}`), position: index, createdAt: now, updatedAt: now
    }));
    const products = buildPracticeProducts();
    const availableProducts: AvailableProduct[] = products.map(p => ({
        productKey: inventoryKey(PRACTICE_PLATFORM, p.articleId, p.name), productName: p.name, latestUnitPrice: p.price, pricePerStdUnit: p.price / p.quantityInfo!.stdAmount, stdUnit: 'g', platform: PRACTICE_PLATFORM, category: p.category, mainCategory: 'FOOD', latestPurchaseDate: now.toISOString(), assignedToSectionIds: sections.map(s => s.id), assignedToSectionNames: sections.map(s => s.sectionName)
    }));
    const sectionProducts: Record<string, InventorySectionProduct[]> = {};
    sections.forEach((s, si) => { sectionProducts[s.id] = availableProducts.map((p, pi) => ({
        id: id(`assignment-${si}-${pi}`), inventorySectionId: s.id, restaurantId, productKey: pi === 7 && si === 0 ? `custom:${id('unlinked')}` : p.productKey, productName: p.productName, position: pi, createdAt: now, stdUnit: 'g', countUnit: i18next.t('onboarding.inventory.practice.pack'), countUnitToStdFactor: 1000, additionalCountUnits: [], source: pi === 7 && si === 0 ? 'custom' : 'order', inventoryCategoryId: id(`category-${pi % 3}`), inventoryCategoryName: i18next.t(`onboarding.inventory.practice.category${pi % 3}`), pricePerStdUnit: p.pricePerStdUnit, pricePerCountUnit: p.pricePerStdUnit! * 1000, platform: PRACTICE_PLATFORM
    })); });
    const countsHistory: InventoryCount[] = [];
    const sessionSummaries: InventorySessionSummary[] = [];
    for (let age = 0; age < 4; age++) {
        const countedAt = new Date(now);
        countedAt.setDate(now.getDate() - age * 7);
        const sessionId = id(`session-${age}`);
        sections.forEach((s, si) => {
            const items = sectionProducts[s.id].filter((_, pi) => pi !== 6).map((p, pi) => {
                const count = age === 0 ? (pi === 0 && si === 0 ? 22 : 1 + pi % 3) : (pi === 0 ? 2 : 12 + pi);
                return {
                    productKey: p.productKey, productName: p.productName, count, countUnit: p.countUnit, stdUnit: p.stdUnit, countInStdUnit: count * 1000, pricePerStdUnit: p.pricePerStdUnit!, value: count * 1000 * p.pricePerStdUnit!
                };
            });
            countsHistory.push({
                id: id(`count-${age}-${si}`), restaurantId, inventorySectionId: s.id, sectionNameSnapshot: s.sectionName, countedAt, countedByUserId: null, totalValue: items.reduce((n, p) => n + p.value, 0), items, sessionId, groupId: group.id, groupNameSnapshot: group.name
            });
        });
        const counts = countsHistory.filter(c => c.sessionId === sessionId);
        sessionSummaries.push({
            sessionId, completedAt: countedAt, sectionCount: counts.length, totalValue: counts.reduce((n, c) => n + c.totalValue, 0), countIds: counts.map(c => c.id), groupId: group.id, groupName: group.name
        });
    }
    const core = {
        restaurantId, groups: [group], sections, countsHistory, sectionProducts, availableProducts, sessionSummaries, canonicalUnitsByGroup: { [group.id]: new Map(availableProducts.map(p => [p.productKey, { countUnit: i18next.t('onboarding.inventory.practice.pack'), stdPerCountUnit: 1000, stdUnit: 'g' }])) }
    };
    const selection = { groupId: null, sessionId: null };
    dataset = { ...core, countReview: {
            data: review(core, selection), selection, isLoading: false, loadedKey: 'practice'
        } };
    publish();
}
export function endInventoryPracticeData() {
    dataset = null;
    publish();
}
export function selectInventoryPractice(selection: InventoryPracticeSelection) {
    if (!dataset)
        return;
    // Customer tours explore the copied review; arbitrary selections would require
    // a fresh server calculation and must not run through the synthetic fixture builder.
    if (dataset.source === 'customer') return;
    rebuild({ ...dataset, countReview: { ...dataset.countReview, selection } });
}
export function updateInventoryPracticeCount(countId: string, items: {
    productKey: string;
    count: number;
}[], add = false): InventoryCount {
    if (!dataset)
        throw new Error('Inventory practice session ended');
    if (dataset.source === 'customer') throw new Error('Customer count snapshots are read-only');
    const count = dataset.countsHistory.find(c => c.id === countId);
    if (!count)
        throw new Error('Unknown practice count');
    const nextItems = [...count.items];
    for (const update of items) {
        if (!Number.isFinite(update.count) || update.count < 0)
            throw new Error('Invalid practice count');
        const index = nextItems.findIndex(i => i.productKey === update.productKey);
        const assignment = dataset.sectionProducts[count.inventorySectionId!]!.find(p => p.productKey === update.productKey);
        if (!assignment)
            throw new Error('Unknown practice product');
        if (index < 0 && !add)
            throw new Error('Unknown counted practice product');
        const old = nextItems[index];
        const factor = old && old.count > 0 ? old.countInStdUnit / old.count : assignment.countUnitToStdFactor;
        const price = old?.pricePerStdUnit ?? assignment.pricePerStdUnit!;
        const item = {
            productKey: update.productKey, productName: old?.productName ?? assignment.productName, count: update.count, countUnit: old?.countUnit ?? assignment.countUnit, stdUnit: old?.stdUnit ?? assignment.stdUnit, countInStdUnit: update.count * factor, pricePerStdUnit: price, value: update.count * factor * price
        };
        if (index < 0)
            nextItems.push(item);
        else
            nextItems[index] = item;
    }
    const next = { ...count, items: nextItems, totalValue: nextItems.reduce((s, i) => s + i.value, 0) };
    rebuild({ ...dataset, countsHistory: dataset.countsHistory.map(c => c.id === countId ? next : c) });
    return next;
}
export function relinkInventoryPractice(productId: string, payload: {
    productKey: string;
    source: 'order' | 'custom' | 'subrecipe';
}): InventorySectionProduct {
    if (!dataset)
        throw new Error('Inventory practice session ended');
    if (dataset.source === 'customer') throw new Error('Customer count snapshots are read-only');
    const assignment = Object.values(dataset.sectionProducts).flat().find(p => p.id === productId);
    const target = dataset.availableProducts.find(p => p.productKey === payload.productKey);
    if (!assignment || !target)
        throw new Error('Unknown practice link');
    const next = { ...assignment, ...payload, productName: target.productName };
    const countsHistory = dataset.countsHistory.map(c => c.inventorySectionId !== assignment.inventorySectionId ? c : { ...c, items: c.items.map(i => i.productKey === assignment.productKey ? { ...i, productKey: target.productKey, productName: target.productName } : i) });
    rebuild({ ...dataset, countsHistory, sectionProducts: { ...dataset.sectionProducts, [assignment.inventorySectionId]: dataset.sectionProducts[assignment.inventorySectionId].map(p => p.id === productId ? next : p) } });
    return next;
}
